import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useQuery } from '@apollo/client'
import Swal from 'sweetalert2'
import client from '@graphql/client'

import { Card, Button } from '@lib'
import { Form, Input, FormError, Submit } from '@shared/Form'
import { array, object } from '@utils'

import templatesGql from '@graphql/queries/templates'
import templateFieldValuesGql from '@graphql/queries/template-field-value'
import templateFieldAllowedValuesGql from '@graphql/queries/template-field-allowed-value'
import createTemplateFieldAllowedValue from '@graphql/mutators/create-template-field-allowed-value'
import updateTemplateFieldAllowedValue from '@graphql/mutators/update-template-field-allowed-value'

const TemplateFieldValueForm = ({ id, onSave }) => {
  const [input, setInput] = useState({})
  const [formOptions, setFormOptions] = useState({})
  const [loading, setLoading] = useState(true)
  const [restricted, setRestricted] = useState({})
  const [inputErrors, setInputErrors] = useState({})

  useQuery(templatesGql, {
    fetchPolicy: 'network-only',
    onCompleted: ({ templates }) => {
      const options = templates.map(template => ({ value: template.id, text: template.name, fields: template.templateFields }))
      setFormOptions((prevState) => ({
        ...prevState,
        templateOptions: options
      })
      )
    }
  })

  useQuery(templateFieldValuesGql, {
    fetchPolicy: 'network-only',
    onCompleted: ({ templateFieldValues }) => {
      const options = templateFieldValues.map(templateFieldValue => ({ value: templateFieldValue.id, text: templateFieldValue.value, templateFieldId: templateFieldValue.templateFieldId }))
      setFormOptions((prevState) => ({
        ...prevState,
        templateFieldValueOptions: options
      })
      )
    }
  })

  useEffect(() => {
    if (id && loading) {
      client.query({
        query: templateFieldAllowedValuesGql,
        variables: { id }
      }).then(({ data }) => {
        if (data.templateFieldAllowedValues) {
          let { templateFieldAllowedValues } = data
          if (templateFieldAllowedValues.constructor === Array) {
            templateFieldAllowedValues = templateFieldAllowedValues[0]
          }
          const templateId = templateFieldAllowedValues.templateField.template.id
          const templateFieldId = templateFieldAllowedValues.templateField.id
          const templateFieldValueId = templateFieldAllowedValues.templateFieldValue.id
          const allowedValue = templateFieldAllowedValues.allowedValue

          setInput({
            templateId,
            templateFieldId,
            templateFieldValueId,
            allowedValue
          })

          setLoading(false)
        } else {
          setRestricted(true)
        }
      })
    }
  }, [loading])

  if (id && loading) {
    if (restricted === true && !input.id) {
      // User should not have access
    } else {
      return null
    }
  }

  const handleInputChange = (name, value) => {
    const newInput = { ...input }

    if (name === 'templateId') {
      delete newInput.templateFieldId
      delete newInput.templateFieldValueId
    } else if (name === 'templateFieldId') {
      delete newInput.templateFieldValueId
    }

    newInput[name] = value
    setInput(newInput)

    const updatedError = inputErrors
    delete updatedError[name]
    setInputErrors(updatedError)
  }

  const handleSave = (evt) => {
    if (evt) {
      evt.preventDefault()
    }

    const updatedInput = { ...input }

    const feErrors = {}

    if (!input.templateId) {
      feErrors.templateId = [{
        message: 'Template is required'
      }]
    }
    if (!input.templateFieldId) {
      feErrors.templateFieldId = [{
        message: 'Template field is required'
      }]
    }
    if (!input.templateFieldValueId) {
      feErrors.templateFieldValueId = [{
        message: 'Equivalent value is required'
      }]
    }
    if (!input.allowedValue) {
      feErrors.allowedValue = [{
        message: 'Allowed value is required'
      }]
    }

    if (!object.keys(feErrors).length) {
      if (id) {
        // Function updateTemplateFieldValue
        updatedInput.id = id

        updateTemplateFieldAllowedValue(updatedInput).then(({ data, extensions }) => {
          if (data.updateTemplateFieldAllowedValue) {
            setInputErrors({})
            onSave()
          } else if (extensions && extensions.errors) {
            extensions.errors = extensions.errors.filter(e => e.path !== 'sqlError')
            setInputErrors(array.groupBy(extensions.errors, 'path'))
          } else {
            Swal.fire({
              text: 'Record already exists!',
              icon: 'error',
              showConfirmButton: false,
              timer: 3000
            })
          }
        })
      } else {
        // Function createTemplateFieldValue
        createTemplateFieldAllowedValue(input).then(({ data, extensions }) => {
          if (data.createTemplateFieldAllowedValue) {
            setInputErrors({})
            onSave()
          } else if (extensions && extensions.errors) {
            extensions.errors = extensions.errors.filter(e => e.path !== 'sqlError')
            setInputErrors(array.groupBy(extensions.errors, 'path'))
          } else {
            Swal.fire({
              text: 'Record already exists!',
              icon: 'error',
              showConfirmButton: false,
              timer: 3000
            })
          }
        })
      }
    } else {
      setInputErrors(feErrors)
    }
  }

  return (
    <Card>
      <Form className='field-value-form' onSubmit={handleSave}>
        <FormError errors={inputErrors} />
        <Input
          className='template'
          placeholder='Select Template'
          type='select'
          label='Template'
          options={formOptions.templateOptions}
          value={input.templateId}
          onChange={(value) => handleInputChange('templateId', value)}
          errors={inputErrors.templateId}
        />
        <Input
          className='templateField'
          placeholder='Select Template Field'
          type='select'
          label='Template Field'
          options={formOptions.templateOptions?.find(tOpt => tOpt.value === input.templateId)?.fields.filter(f => f.isValidate).map(({ id, fieldName }) => ({ value: id, text: fieldName }))}
          value={input.templateFieldId}
          onChange={(value) => handleInputChange('templateFieldId', value)}
          errors={inputErrors.templateFieldId}
          disabled={!input.templateId}
        />
        <Input
          className='equivalentValue'
          placeholder='Select Equivalent Value'
          type='select'
          label='Equivalent Value'
          options={formOptions.templateFieldValueOptions?.filter(fvo => fvo.templateFieldId === input.templateFieldId)}
          value={input.templateFieldValueId}
          onChange={(value) => handleInputChange('templateFieldValueId', value)}
          errors={inputErrors.templateFieldValueId}
          disabled={!input.templateFieldId}
        />
        <Input
          className='allowedValue'
          type='text'
          label='Allowed Value'
          value={input.allowedValue}
          onChange={(value) => handleInputChange('allowedValue', value)}
          errors={inputErrors.allowedValue}
        />

        <Submit className='right'>
          <Button
            type='submit'
            className='form-button icon-left'
            icon={`${id ? '' : 'fas fa-plus'}`}
            text={`${id ? 'Edit' : 'Add'} Field Value`}
          />
        </Submit>
      </Form>
    </Card>
  )
}

TemplateFieldValueForm.propTypes = {
  id: PropTypes.string,
  onSave: PropTypes.func
}

TemplateFieldValueForm.defaultProps = {
  id: '',
  onSave: () => {}
}

export default TemplateFieldValueForm
